import { useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { get, post } from "../api";
import { STAGE_LABEL, STAGES } from "../api";
import type { MonthFigures, MonthRun, RevenueOverview, RevenueTotal, Stage, SubjectRevenue } from "../api";
import { useAuth } from "../auth";
import { CoverFilter, UNASSIGNED, useCsms } from "../CoverFilter";
import { Time } from "../Time";
import MonthBrief from "../revenue/MonthBrief";
import PacePanel from "../revenue/PacePanel";
import RevenueChart from "../revenue/RevenueChart";
import { StatusPill } from "../revenue/StatusPill";
import { minutes, money, monthLabel, monthParam, vmShare } from "../revenue/format";

/**
 * Revenue across every customer the caller covers, month by month.
 *
 * The month strip picks the month; everything below it is that month. A
 * closed month is a frozen statement and does not move; the current one is
 * priced live from the mirror and carries a run-rate projection, because a
 * month-to-date figure on the 5th always reads as a collapse.
 *
 * Totals are per currency. Boost is billed in ringgit, and adding it to
 * rupees would produce a number that is not anybody's revenue.
 *
 * The unmapped tenants are in the response only for an admin; the server
 * not sending them is the control, this page not drawing them is not.
 */
export default function Revenue() {
  const { me } = useAuth();
  const isAdmin = me?.user.is_admin ?? false;
  const csms = useCsms(isAdmin);
  const [params, setParams] = useSearchParams();
  const user = isAdmin ? params.get("user") : null;

  const [data, setData] = useState<RevenueOverview | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [nonce, setNonce] = useState(0);

  useEffect(() => {
    setData(null);
    setError(null);
    const q = new URLSearchParams();
    if (user) q.set("user", user);
    void get<RevenueOverview>(`/api/revenue?${q}`)
      .then(setData)
      .catch((e) => setError(e.message));
  }, [user, nonce]);

  function setParam(key: string, value: string) {
    const next = new URLSearchParams(params);
    if (value) next.set(key, value);
    else next.delete(key);
    setParams(next, { replace: true });
  }

  const month = params.get("m") ? `${params.get("m")}-01` : (data?.months[0] ?? "");
  const byStage = params.get("by") === "stage";
  const currencies = useMemo(() => {
    const set = new Set<string>();
    for (const t of data?.customer_totals ?? []) set.add(t.currency);
    for (const t of data?.unmapped_totals ?? []) set.add(t.currency);
    return [...set].sort((a, b) => (a === "INR" ? -1 : b === "INR" ? 1 : a.localeCompare(b)));
  }, [data]);

  const whose =
    user === UNASSIGNED
      ? "customers nobody covers"
      : user
        ? `${csms.find((u) => u.id === user)?.name ?? "their"}'s customers`
        : isAdmin
          ? "all customers"
          : "your customers";

  return (
    <div className="wrap wide">
      <h1>Revenue</h1>
      <p className="sub">
        AI voice bot and dialer charges for {whose}, priced per call from each customer's plan.
      </p>

      <div className="controls" style={{ justifyContent: "space-between" }}>
        <SyncLine data={data} />
        {isAdmin && (
          <CoverFilter value={user ?? ""} csms={csms} onChange={(v) => setParam("user", v)} />
        )}
      </div>

      {error && <div className="error">{error}</div>}
      {!data && !error && <div className="empty">Loading…</div>}

      {data && (
        <>
          <MonthStrip
            months={data.months}
            totals={data.customer_totals}
            unmapped={data.unmapped_totals}
            selected={month}
            onSelect={(m) => setParam("m", m === data.months[0] ? "" : monthParam(m))}
          />

          {currencies.map((c) => (
            <RevenueChart
              key={c}
              currency={c}
              months={data.months}
              totals={[...data.customer_totals, ...(data.unmapped_totals ?? [])].filter(
                (t) => t.currency === c,
              )}
              selected={month}
            />
          ))}

          <MonthKpis
            month={month}
            totals={data.customer_totals.filter((t) => t.month === month)}
            label="Customers"
          />

          {/* The live month has no brief until it freezes: it gets its pace
              against the same days of last month instead. */}
          {month === data.months[0] ? (
            <PacePanel user={user} />
          ) : (
            <MonthBrief month={month} user={user} />
          )}

          <div className="controls" style={{ justifyContent: "space-between" }}>
            <h2 className="rev-h2" style={{ margin: 0 }}>
              Customers
            </h2>
            <div className="fgroup">
              <span className="dim" id="group-label">
                Group by
              </span>
              <div className="picker" role="group" aria-labelledby="group-label" style={{ margin: 0 }}>
                {(["none", "stage"] as const).map((g) => (
                  <button
                    key={g}
                    type="button"
                    className={(g === "stage") === byStage ? "on" : ""}
                    aria-pressed={(g === "stage") === byStage}
                    onClick={() => setParam("by", g === "stage" ? "stage" : "")}
                  >
                    {g === "none" ? "Nothing" : "Stage"}
                  </button>
                ))}
              </div>
            </div>
          </div>

          {byStage ? (
            <ByStage
              customers={data.customers}
              month={month}
              linkTo={(s) => `/c/${s.slug}/revenue?m=${monthParam(month)}`}
            />
          ) : (
            <SubjectTable
              title="Customers"
              subjects={data.customers}
              month={month}
              linkTo={(s) => `/c/${s.slug}/revenue?m=${monthParam(month)}`}
            />
          )}

          {isAdmin && data.unmapped && (
            <>
              <h2 className="rev-h2">Tenants mapped to no customer</h2>
              <p className="dim">
                Estimated at the default plan. Map them in{" "}
                <Link to="/admin/domains?filter=unclaimed">Admin › Platform domains</Link> to price
                them under a contract.
              </p>
              <MonthKpis
                month={month}
                totals={(data.unmapped_totals ?? []).filter((t) => t.month === month)}
                label="Unmapped"
              />
              <SubjectTable title="Unmapped tenants" subjects={data.unmapped} month={month} />
            </>
          )}

          {isAdmin && (
            <Recompute
              month={month}
              current={data.months[0]}
              onDone={() => setNonce((n) => n + 1)}
            />
          )}
        </>
      )}
    </div>
  );
}

function SyncLine({ data }: { data: RevenueOverview | null }) {
  if (!data || data.syncs.length === 0) {
    return <span className="dim">{data ? "The call-length mirror has never been read." : ""}</span>;
  }
  return (
    <span className="dim">
      {data.syncs.map((s, i) => (
        <span key={s.region}>
          {i > 0 && " · "}
          {s.region} read{" "}
          {s.last_success_at ? <Time value={s.last_success_at} /> : "never"}
          {s.consecutive_failures > 0 && (
            <span className="rev-warn" title={s.last_error ?? ""}>
              {" "}
              ({s.consecutive_failures} failed since)
            </span>
          )}
        </span>
      ))}
    </span>
  );
}

function MonthStrip({
  months,
  totals,
  unmapped,
  selected,
  onSelect,
}: {
  months: string[];
  totals: RevenueTotal[];
  unmapped?: RevenueTotal[];
  selected: string;
  onSelect: (m: string) => void;
}) {
  return (
    <div className="rev-strip" role="tablist" aria-label="Month">
      {months.map((m, i) => {
        const inr = totals.find((t) => t.month === m && t.currency === "INR");
        const extra = unmapped?.find((t) => t.month === m && t.currency === "INR");
        const status = i === 0 ? "live" : inr && inr.frozen > 0 ? "frozen" : inr ? "pending" : "not_computed";
        return (
          <button
            key={m}
            role="tab"
            aria-selected={m === selected}
            className={m === selected ? "on" : ""}
            onClick={() => onSelect(m)}
          >
            <span className="rev-strip-m">
              {monthLabel(m)} {i === 0 && <span className="dim">to date</span>}
            </span>
            <span className="rev-strip-v">{inr ? money(inr.total_amount, "INR") : "—"}</span>
            <span className="rev-strip-s">
              <StatusPill status={status} />
              {extra && Number(extra.total_amount) > 0 && (
                <span className="dim" title="Unmapped tenants, estimated">
                  +{money(extra.total_amount, "INR")}
                </span>
              )}
            </span>
          </button>
        );
      })}
    </div>
  );
}

function MonthKpis({
  month,
  totals,
  label,
}: {
  month: string;
  totals: RevenueTotal[];
  label: string;
}) {
  if (totals.length === 0) {
    return <div className="empty">Nothing priced for {monthLabel(month)}.</div>;
  }
  // Rupees first: the business reads it as the headline, ringgit beside it.
  const ordered = [...totals].sort((a, b) => (a.currency === "INR" ? -1 : b.currency === "INR" ? 1 : 0));
  return (
    <>
      {ordered.map((t) => (
        <div className="kpis" key={t.currency} aria-label={`${label}, ${t.currency}`}>
          <Tile
            label={`Total (${t.currency})`}
            value={money(t.total_amount, t.currency)}
            exact={money(t.total_amount, t.currency, true)}
            hint="Bot plus dialer, at each customer's tier for the month"
          />
          <Tile
            label="AI voice bot"
            value={money(t.bot_amount, t.currency)}
            exact={money(t.bot_amount, t.currency, true)}
            hint={
              t.bot_flat_calls
                ? `${money(Number(t.bot_amount) - Number(t.bot_flat_amount), t.currency, true)} per minute + ${money(t.bot_flat_amount, t.currency, true)} for ${t.bot_flat_calls} calls charged per call`
                : undefined
            }
          />
          <Tile label="Dialer" value={money(t.dialer_amount, t.currency)} exact={money(t.dialer_amount, t.currency, true)} />
          {t.bot_flat_calls + t.dialer_flat_calls > 0 && (
            <Tile
              label="Flat calls"
              value={new Intl.NumberFormat("en-IN").format(t.bot_flat_calls + t.dialer_flat_calls)}
              exact={money(Number(t.bot_flat_amount) + Number(t.dialer_flat_amount), t.currency, true)}
              hint="Calls charged per call rather than per minute; they still count toward usage by their pulse"
            />
          )}
          <Tile
            label="Billable bot min"
            value={minutes(t.bot_billable_secs)}
            hint="After each plan's pulse rounding; voicemail included"
          />
          <Tile
            label="Voicemail share"
            value={vmShare(t)}
            hint="Billable minutes that reached an answering machine rather than a person"
          />
          {t.projected_amount !== null && (
            <Tile
              label="Projected month"
              value={money(t.projected_amount, t.currency)}
              hint="If the rest of the month runs like the part so far, each customer at the tier that projection reaches"
            />
          )}
          <Tile
            label="Below commitment"
            value={String(t.below_mmc)}
            hint="Customers whose usage has not reached their lowest tier (the minimum monthly commitment)"
          />
        </div>
      ))}
    </>
  );
}

function Tile({ label, value, exact, hint }: { label: string; value: string; exact?: string; hint?: string }) {
  return (
    <div className="kpi" title={hint}>
      <div className="kpi-label">{label}</div>
      <div className="kpi-value" title={exact}>
        {value}
      </div>
    </div>
  );
}

type StageKey = Stage | "none";
const STAGE_ORDER: StageKey[] = [...STAGES, "none"];
const stageName = (k: StageKey) => (k === "none" ? "No stage" : STAGE_LABEL[k]);

/**
 * The month's customers grouped by pipeline stage: a summary with each
 * stage's share, then the customer table under each stage.
 *
 * Stage is the customer's stage *today* -- it is not versioned -- so a
 * past month's revenue sits under the stage held now, which the page says.
 * Totals stay per currency: a stage with a ringgit customer gets a second
 * row rather than a sum of two currencies.
 */
function ByStage({
  customers,
  month,
  linkTo,
}: {
  customers: SubjectRevenue[];
  month: string;
  linkTo: (s: SubjectRevenue) => string;
}) {
  const groups = new Map<StageKey, SubjectRevenue[]>();
  for (const c of customers) {
    const k: StageKey = c.stage ?? "none";
    groups.set(k, [...(groups.get(k) ?? []), c]);
  }
  const present = STAGE_ORDER.filter((k) => groups.has(k));

  type Row = { stage: StageKey; currency: string; n: number; bot_secs: number; vm_secs: number; bot: number; dialer: number; total: number; projected: number | null };
  const rows: Row[] = [];
  const grand = new Map<string, number>();
  for (const k of present) {
    const byCur = new Map<string, Row>();
    for (const c of groups.get(k)!) {
      const f = c.months.find((m) => m.month === month);
      if (!f || f.status === "not_computed") continue;
      const r =
        byCur.get(f.currency) ??
        ({ stage: k, currency: f.currency, n: 0, bot_secs: 0, vm_secs: 0, bot: 0, dialer: 0, total: 0, projected: null } as Row);
      r.n += 1;
      r.bot_secs += f.bot_billable_secs;
      r.vm_secs += f.voicemail_billable_secs;
      r.bot += Number(f.bot_amount);
      r.dialer += Number(f.dialer_amount);
      r.total += Number(f.total_amount);
      if (f.projection) r.projected = (r.projected ?? 0) + Number(f.projection.total_amount);
      byCur.set(f.currency, r);
    }
    for (const r of byCur.values()) {
      rows.push(r);
      grand.set(r.currency, (grand.get(r.currency) ?? 0) + r.total);
    }
  }
  const anyProjection = rows.some((r) => r.projected !== null);

  return (
    <>
      <div className="ctable">
        <table aria-label="Revenue by stage">
          <thead>
            <tr>
              <th>Stage</th>
              <th className="n">Customers</th>
              <th className="n">Bot min</th>
              <th className="n">VM</th>
              <th className="n">Bot</th>
              <th className="n">Dialer</th>
              <th className="n">Total</th>
              <th className="n" title="Share of this currency's total for the month">
                Share
              </th>
              {anyProjection && <th className="n">Projected</th>}
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <tr key={`${r.stage}-${r.currency}`}>
                <td>
                  <a href={`#stage-${r.stage}`}>{stageName(r.stage)}</a>
                  {r.currency !== "INR" && <span className="tz">{r.currency}</span>}
                </td>
                <td className="n">{r.n}</td>
                <td className="n">{minutes(r.bot_secs)}</td>
                <td className="n">{vmShare({ voicemail_billable_secs: r.vm_secs, bot_billable_secs: r.bot_secs })}</td>
                <td className="n">{money(r.bot, r.currency)}</td>
                <td className="n">{r.dialer ? money(r.dialer, r.currency) : "—"}</td>
                <td className="n" title={money(r.total, r.currency, true)}>
                  <strong>{money(r.total, r.currency)}</strong>
                </td>
                <td className="n">
                  {grand.get(r.currency) ? `${((r.total / grand.get(r.currency)!) * 100).toFixed(1)}%` : "—"}
                </td>
                {anyProjection && (
                  <td className="n">{r.projected !== null ? `~${money(r.projected, r.currency)}` : "—"}</td>
                )}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <p className="dim">
        Grouped by each customer's stage today; stages are not kept historically, so a past month is
        shown under the current stage.
      </p>

      {present.map((k) => (
        <section key={k} id={`stage-${k}`}>
          <h3 className="rev-h3">
            {stageName(k)} <span className="dim">· {groups.get(k)!.length}</span>
          </h3>
          <SubjectTable title="Customers" subjects={groups.get(k)!} month={month} linkTo={linkTo} />
        </section>
      ))}
    </>
  );
}

function SubjectTable({
  title,
  subjects,
  month,
  linkTo,
}: {
  title: string;
  subjects: SubjectRevenue[];
  month: string;
  linkTo?: (s: SubjectRevenue) => string;
}) {
  const rows = subjects
    .map((s) => ({ s, f: s.months.find((m) => m.month === month) }))
    .filter((r): r is { s: SubjectRevenue; f: MonthFigures } => !!r.f)
    .filter((r) => r.f.status !== "not_computed" || linkTo)
    .sort((a, b) => Number(b.f.total_amount) - Number(a.f.total_amount));
  // A customer with nothing this month is still a row worth seeing -- the
  // silence is the news. A tenant nobody owns with no calls is just noise.
  const shown = linkTo ? rows : rows.filter((r) => r.f.calls > 0);
  const hidden = rows.length - shown.length;

  if (shown.length === 0) return <div className="empty">No {title.toLowerCase()} with calls this month.</div>;

  return (
    <div className="ctable">
      <table aria-label={title}>
        <thead>
          <tr>
            <th>{title === "Customers" ? "Customer" : "Tenant"}</th>
            <th>Tier</th>
            <th className="n" title="Billable AI-bot minutes after pulse rounding">
              Bot min
            </th>
            <th className="n" title="Share of billable bot minutes that were voicemail">
              VM
            </th>
            <th className="n">Bot</th>
            <th className="n">Dialer</th>
            <th className="n">Total</th>
            <th>Status</th>
          </tr>
        </thead>
        <tbody>
          {shown.map(({ s, f }) => (
            <tr key={JSON.stringify(s.subject)}>
              <td>
                {linkTo ? <Link to={linkTo(s)}>{s.name}</Link> : <span className="cid">{s.name}</span>}
                {s.subject.type === "tenant" && <span className="tz">{s.subject.region}</span>}
                {s.detail && s.detail !== s.name && <div className="dim">{s.detail}</div>}
              </td>
              <td>
                {f.status === "not_computed" ? (
                  "—"
                ) : (
                  <>
                    {f.tier_label}
                    {f.projection && f.projection.tier_label !== f.tier_label && (
                      <span className="dim" title="The tier the month-end projection reaches">
                        {" "}
                        → {f.projection.tier_label}
                      </span>
                    )}
                    {f.below_mmc_minutes !== null && (
                      <div className="rev-warn" title="Shown, not charged: usage below the lowest tier">
                        {minutes(Number(f.below_mmc_minutes) * 60)} min below commitment
                      </div>
                    )}
                  </>
                )}
              </td>
              <td className="n">{minutes(f.bot_billable_secs)}</td>
              <td className="n">{vmShare(f)}</td>
              <td className="n">{money(f.bot_amount, f.currency || "INR")}</td>
              <td className="n">{Number(f.dialer_amount) ? money(f.dialer_amount, f.currency) : "—"}</td>
              <td className="n" title={money(f.total_amount, f.currency || "INR", true)}>
                <strong>{money(f.total_amount, f.currency || "INR")}</strong>
                {f.projection && (
                  <div className="dim" title="Projected month-end">
                    ~{money(f.projection.total_amount, f.currency)}
                  </div>
                )}
              </td>
              <td>
                <StatusPill status={f.status} />
                {f.estimated && f.status !== "not_computed" && (
                  <span
                    className="pill rev-est"
                    title={
                      s.has_plan
                        ? "No plan in effect for this month; priced at the default rate"
                        : "No plan entered; priced at the default rate"
                    }
                  >
                    {s.has_plan ? "Before plan" : "No plan"}
                  </span>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      {hidden > 0 && <p className="dim">{hidden} more with no calls this month.</p>}
    </div>
  );
}

/** Admin: re-price a closed month from the mirror, appending a snapshot. */
function Recompute({ month, current, onDone }: { month: string; current: string; onDone: () => void }) {
  const [open, setOpen] = useState(false);
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState<MonthRun | null>(null);
  const [error, setError] = useState<string | null>(null);

  if (month === current) return null;

  async function run() {
    setBusy(true);
    setError(null);
    try {
      const r = await post<MonthRun>("/api/admin/revenue/recompute", {
        month: monthParam(month),
        reason,
      });
      setResult(r);
      setOpen(false);
      setReason("");
      onDone();
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="rev-recompute">
      {!open ? (
        <button className="btn tiny" onClick={() => setOpen(true)}>
          Recompute {monthLabel(month)}…
        </button>
      ) : (
        <div className="controls">
          <input
            placeholder="Why (recorded in the audit log)"
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            style={{ flex: "1 1 320px" }}
          />
          <button disabled={busy || !reason.trim()} onClick={run}>
            {busy ? "Recomputing…" : `Recompute every subject for ${monthLabel(month)}`}
          </button>
          <button className="btn tiny" onClick={() => setOpen(false)}>
            Cancel
          </button>
        </div>
      )}
      <p className="dim">
        Re-prices the month from the mirror with today's plans and mappings, and stores a new
        snapshot; the previous one is kept. To re-read the month from Redshift first, run{" "}
        <code>make revenue-month M={monthParam(month)}</code>.
      </p>
      {error && <div className="error">{error}</div>}
      {result && (
        <p className="dim">
          Stored {result.stored} statement(s).
          {result.skipped.length > 0 && ` Skipped ${result.skipped.length}: ${result.skipped.join("; ")}`}
        </p>
      )}
    </div>
  );
}
